import { Component, inject, OnDestroy, OnInit, signal, computed, ChangeDetectionStrategy } from '@angular/core';
import { DatePipe } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { DividerModule } from 'primeng/divider';
import { ProgressBar } from 'primeng/progressbar';
import { ProgressSpinner } from 'primeng/progressspinner';
import { DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { MessageService } from 'primeng/api';
import { Subject, interval, takeUntil, switchMap, firstValueFrom, filter, tap } from 'rxjs';
import {
  ApprovedSystemsService,
  BulkRenewalStatusResponse,
  StartBulkRenewalResponse,
} from '../../services/approved-systems.service';

@Component({
  selector: 'app-bulk-renewal-dialog',
  standalone: true,
  imports: [ButtonModule, DividerModule, ProgressBar, ProgressSpinner],
  providers: [DatePipe],
  templateUrl: './bulk-renewal-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./bulk-renewal-dialog.component.scss'],
})
export class BulkRenewalDialogComponent implements OnInit, OnDestroy {
  private dialogRef = inject(DynamicDialogRef);
  private dialogConfig = inject(DynamicDialogConfig);
  private messageService = inject(MessageService);
  private datePipe = inject(DatePipe);
  private approvedSystemsService = inject(ApprovedSystemsService);
  private destroy$ = new Subject<void>();

  // Initial count
  totalFinalSystems = signal(0);
  loadingCount = signal(true);

  // Batch state
  batchId = signal<string | null>(null);
  status = signal<BulkRenewalStatusResponse | null>(null);
  newExpiryDate = signal<string | null>(null);

  // Loading states
  starting = signal(false);
  polling = signal(false);

  // Computed values
  progressPercent = computed(() => {
    const s = this.status();
    if (!s || s.totalCount === 0) return 0;
    return Math.round((s.completedCount / s.totalCount) * 100);
  });

  isComplete = computed(() => this.status()?.isComplete ?? false);

  hasFailures = computed(() => (this.status()?.failedCount ?? 0) > 0);

  canClose = computed(() => !this.starting() && (this.isComplete() || !this.batchId()));

  nextYear = new Date().getFullYear() + 1;

  ngOnInit(): void {
    this.loadFinalSystemsCount();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadFinalSystemsCount(): void {
    this.approvedSystemsService.getFinalSystemsCount().subscribe({
      next: (response) => {
        this.totalFinalSystems.set(response.count);
        this.loadingCount.set(false);
      },
      error: (err) => {
        console.error('Failed to load final systems count:', err);
        this.loadingCount.set(false);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load eligible systems count',
          life: 5000,
        });
      },
    });
  }

  async startRenewal(): Promise<void> {
    if (this.starting() || this.batchId()) return;

    this.starting.set(true);

    try {
      const response: StartBulkRenewalResponse = await firstValueFrom(this.approvedSystemsService.startBulkRenewal());

      const totalToProcess = response.totalSystemsRenewed + response.totalSystemsRegenerated;

      // Handle case where no systems need processing
      if (totalToProcess === 0) {
        this.messageService.add({
          severity: 'info',
          summary: 'No Action Required',
          detail: 'All certificates are already up to date.',
          life: 5000,
        });
        return;
      }

      this.batchId.set(response.batchId);
      this.newExpiryDate.set(response.newExpiryDate);

      // Hide X button while processing
      this.dialogConfig.closable = false;

      // Initialize status with initial values
      this.status.set({
        batchId: response.batchId,
        totalCount: totalToProcess,
        pendingCount: totalToProcess,
        processingCount: 0,
        completedCount: 0,
        failedCount: 0,
        queuedCount: totalToProcess,
        isComplete: false,
      });

      this.messageService.add({
        severity: 'info',
        summary: 'Bulk Renewal Started',
        detail: `Processing ${totalToProcess} certificates...`,
        life: 3000,
      });

      // Start polling for status
      this.startPolling();
    } catch (error: unknown) {
      console.error('Failed to start bulk renewal:', error);
      const httpError = error as { error?: { message?: string } };
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: httpError.error?.message || 'Failed to start bulk renewal',
        life: 5000,
      });
    } finally {
      this.starting.set(false);
    }
  }

  private startPolling(): void {
    const batchId = this.batchId();
    if (!batchId) return;

    this.polling.set(true);

    interval(5000)
      .pipe(
        takeUntil(this.destroy$),
        filter(() => !this.isComplete()),
        switchMap(() => this.approvedSystemsService.getBulkRenewalStatus(batchId)),
        tap((status) => {
          this.status.set(status);

          if (status.isComplete) {
            this.polling.set(false);
            this.onComplete(status);
          }
        })
      )
      .subscribe({
        error: (err) => {
          console.error('Failed to get renewal status:', err);
          this.polling.set(false);
        },
      });
  }

  private onComplete(status: BulkRenewalStatusResponse): void {
    // Re-enable X button when complete
    this.dialogConfig.closable = true;

    if (status.failedCount > 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Bulk Renewal Completed with Errors',
        detail: `${status.completedCount} certificates generated, ${status.failedCount} failed`,
        life: 5000,
      });
    } else {
      this.messageService.add({
        severity: 'success',
        summary: 'Bulk Renewal Complete',
        detail: `All ${status.completedCount} certificates generated successfully`,
        life: 3000,
      });
    }
  }

  formatDate(dateStr: string | null): string {
    if (!dateStr) return '-';
    return this.datePipe.transform(new Date(dateStr), 'dd.MM.yyyy', 'UTC') || '-';
  }

  close(): void {
    this.dialogRef.close({
      completed: this.isComplete(),
      status: this.status(),
    });
  }
}
